import { isDebugAPI } from "@litres/utils";
import type { IGlobalApiParams, IReduxRequestNode } from "@litres-libs/types";
import type { AxiosRequestConfig } from "axios";
import { Catalit2PromiseProvider } from "./providers/Catalit2PromiseProvider";
import { Catalit3PromiseProvider } from "./providers/Catalit3PromiseProvider";
import { APIPromiseProvider } from "./providers/APIPromiseProvider";
import { AdvertisingPromiseProvider } from "./providers/AdvertisingPromiseProvider";
import { AxiosPromiseProvider } from "./providers/AxiosPromiseProvider";
import { GUJPromiseProvider } from "./providers/GUJPromiseProvider";
import { CSRFPromiseProvider } from "./providers/CSRFPromiseProvider";

// configs for services
import { foundationConfig } from "./config/services/foundation";
import { advertisingConfig } from "./config/services/advertising";
import { ordersConfig } from "./config/services/orders";
import { trackerConfig } from "./config/services/tracker";

type IGlobalApiParamsWithHeaders<TParams> = Omit<
  IGlobalApiParams<TParams>,
  "headers"
> & {
  /**
   * Требуется обязательно передать глобальные headers
   *
   * @requires
   *
   * @example
   * headers: {
   *   ...getGlobalHeaders(getState() as RootState),
   * },
   */
  headers: IGlobalApiParams<TParams>["headers"];
};

/**
 * Глобальная абстракция над запросами
 */
abstract class RequestAbstract<TParams, TPayload> {
  constructor(params: IGlobalApiParams<TParams>) {
    this.params = params;
    this.payload = {};

    if (isDebugAPI) {
      console.log(
        `%cREQ: ${this.params.provider.id}`,
        "background: #edff88; padding: 2px 12px; color: #888",
        this.params
      );
    }
  }

  params: IGlobalApiParams<TParams>;

  payload: IReduxRequestNode<TParams, TPayload>;

  // public readonly params: IGlobalApiParams<any>;
  public abstract fetch(): Promise<IReduxRequestNode<TParams, TPayload>>;
}

/**
 * Абстракция простого Axios запроса
 */
export class ActionAxios<TParams, TPayload> extends RequestAbstract<
  TParams,
  TPayload
> {
  constructor(params: IGlobalApiParams<TParams>) {
    super(params);
    this.axiosProvider = AxiosPromiseProvider.getInstance();
  }

  private axiosProvider: AxiosPromiseProvider<TParams, TPayload>;

  public fetch = async (): Promise<IReduxRequestNode<TParams, TPayload>> => {
    const axiosProvider = this.axiosProvider;
    const promise = await axiosProvider.makeAxiosRequest(this.params);
    return promise;
  };
}

/**
 * Абстракция над каталит2
 */
export class ActionCat2<TParams, TPayload> extends RequestAbstract<
  TParams,
  TPayload
> {
  constructor(params: IGlobalApiParams<TParams>, config?: AxiosRequestConfig) {
    super(params);
    this.config = config;
    this.catalit2Provider = Catalit2PromiseProvider.getInstance<
      TParams,
      TPayload
    >();
  }

  private catalit2Provider: Catalit2PromiseProvider<TParams, TPayload>;

  private config?: AxiosRequestConfig;

  public fetch = async (): Promise<IReduxRequestNode<TParams, TPayload>> => {
    const catalit2 = this.catalit2Provider;
    const promise = await catalit2.makeAxiosRequest(this.params, this.config);
    return promise;
  };
}

/**
 * Абстракция над каталит3
 */
export class ActionCat3<TParams, TPayload> extends RequestAbstract<
  TParams,
  TPayload
> {
  constructor(params: IGlobalApiParams<TParams>) {
    super(params);
    this.catalit3Provider = Catalit3PromiseProvider.getInstance();
  }

  private catalit3Provider: Catalit3PromiseProvider<TParams, TPayload>;

  public fetch = async (): Promise<IReduxRequestNode<TParams, TPayload>> => {
    const catalit3 = this.catalit3Provider;
    const promise = await catalit3.makeAxiosRequest(this.params);
    return promise;
  };
}

/**
 * Абстракция над Foundation API
 */
export class ActionFoundation<TParams, TPayload> extends RequestAbstract<
  TParams,
  TPayload
> {
  constructor(params: IGlobalApiParamsWithHeaders<TParams>) {
    super(params);
    this.provider = APIPromiseProvider.getInstance(foundationConfig);
  }

  private provider: APIPromiseProvider<TParams, TPayload>;

  public fetch = async (): Promise<IReduxRequestNode<TParams, TPayload>> => {
    const promise = await this.provider.makeAxiosRequest(this.params);
    return promise;
  };
}

/**
 * Абстракция над Advertising API
 */
export class ActionAdvertising<TParams, TPayload> extends RequestAbstract<
  TParams,
  TPayload
> {
  constructor(params: IGlobalApiParamsWithHeaders<TParams>) {
    super(params);
    this.provider = AdvertisingPromiseProvider.getInstance(advertisingConfig);
  }

  private provider: AdvertisingPromiseProvider<TParams, TPayload>;

  public fetch = async (): Promise<IReduxRequestNode<TParams, TPayload>> => {
    const promise = await this.provider.makeAxiosRequest(this.params);
    return promise;
  };
}

/**
 * Абстракция над Orders API
 */
export class ActionOrders<TParams, TPayload> extends RequestAbstract<
  TParams,
  TPayload
> {
  constructor(params: IGlobalApiParamsWithHeaders<TParams>) {
    super(params);
    this.provider = APIPromiseProvider.getInstance(ordersConfig);
  }

  private provider: APIPromiseProvider<TParams, TPayload>;

  public fetch = async (): Promise<IReduxRequestNode<TParams, TPayload>> => {
    const promise = await this.provider.makeAxiosRequest(this.params);
    return promise;
  };
}

/**
 * Абстракция над Tracker API
 */
export class ActionTracker<TParams, TPayload> extends RequestAbstract<
  TParams,
  TPayload
> {
  constructor(params: IGlobalApiParamsWithHeaders<TParams>) {
    super(params);
    this.provider = APIPromiseProvider.getInstance(trackerConfig);
  }

  private provider: APIPromiseProvider<TParams, TPayload>;

  public fetch = async (): Promise<IReduxRequestNode<TParams, TPayload>> => {
    const promise = await this.provider.makeAxiosRequest(this.params);
    return promise;
  };
}
/**
 * Абстракция над GUJ
 */
export class ActionGUJ<TParams, TPayload> extends RequestAbstract<
  TParams,
  TPayload
> {
  constructor(params: IGlobalApiParams<TParams>) {
    super(params);
    this.gujProvider = GUJPromiseProvider.getInstance();
  }

  private gujProvider: GUJPromiseProvider<TParams, TPayload>;

  public fetch = async (): Promise<IReduxRequestNode<TParams, TPayload>> => {
    const guj = this.gujProvider;
    const promise = await guj.makeAxiosRequest(this.params);
    return promise;
  };
}

/**
 * Абстракция над CSRF
 */
export class ActionCSRF<TParams, TPayload> extends RequestAbstract<
  TParams,
  TPayload
> {
  constructor(params: IGlobalApiParams<TParams>) {
    super(params);
    this.csrfProvider = CSRFPromiseProvider.getInstance();
  }

  private csrfProvider: CSRFPromiseProvider<TParams, TPayload>;

  public fetch = async (): Promise<IReduxRequestNode<TParams, TPayload>> => {
    const csrf = this.csrfProvider;
    const promise = await csrf.makeAxiosRequest(this.params);
    return promise;
  };
}

export const Request = {
  Axios: ActionAxios,
  Cat2: ActionCat2,
  Cat3: ActionCat3,
  Foundation: ActionFoundation,
  Advertising: ActionAdvertising,
  Orders: ActionOrders,
  Tracker: ActionTracker,
  GUJ: ActionGUJ,
  CSRF: ActionCSRF,
};

export default Request;
